import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Idle } from '@ng-idle/core';
import { ToastrService } from 'ngx-toastr';
import jwt_decode from "jwt-decode";
import { EventbusConnectionService } from 'app/services/eventbus-connection.service';
import { PanelService } from 'app/shared/panels/chart/Services/panel.service';
import { RouteNavLockService } from 'app/services/route-nav-lock.service';
import { KeyCloakApiService } from 'app/services/key-cloak-api.service';
import { IFeatures, IGetFeaturesByRoles } from 'app/services/graphqlApi.service';
import { RestAPIService } from 'app/services/rest-api.service';
import { getFeaturesByRoles, getFeaturesDetailByFeatures } from 'app/shared/constants/gql';
import { AuthResponse } from '../interfaces/loginModal';

@Injectable({
  providedIn: 'root'
})

export class LoginService {
  token = 'token';
  headers_object: HttpHeaders;
  private _loginSource = new ReplaySubject<boolean>();
  readonly login$ = this._loginSource.asObservable();
  refreshTimeout;
  logoutTimeout;
  idleState = 'Not started.';
  timedOut = false;

  constructor(protected http: HttpClient,
    private toastr: ToastrService,
    private router: Router,
    private idle: Idle,
    private readonly panelService: PanelService,
    private readonly apiService: RestAPIService,
    private readonly routeLockService: RouteNavLockService,
    private readonly eventBusService: EventbusConnectionService,
    private dialog: MatDialog,
    private keyCloakApiService: KeyCloakApiService) {
    this.headers_object = new HttpHeaders().set('Content-Type', 'application/json');
    if (LoginService.isLoggedIn) {
      this._loginSource.next(true);
    } else {
      this._loginSource.next(false);
    }

    // console.log('object :>> login service');
    const token = JSON.parse(localStorage.getItem('Token'));
    if (token) {
      this.setApiTriggerTime(new Date().getTime());
      const time = token.expiresIn ? (token.expiresIn * 1000) : 1.2e6;
      this.setRefreshTokenEvent(time, token.expiresIn);
    }
  }

  setApiTriggerTime(time: number) {
    localStorage.setItem('ApiLastTriggerTime', JSON.stringify(time));
  }

  static getToken() {
    return localStorage.getItem('token');
  }

  login(email: string, password: string): Observable<any> {
    const payload = `grant_type=password&client_id=intellijaui&username=${email}&password=${password}`;
    const headers = new HttpHeaders()
      .set('content-type', 'application/x-www-form-urlencoded');

    return this.keyCloakApiService.auth<any>(payload, 'token', { headers })
      .pipe(
        tap((res: AuthResponse) => {
          this._loginSource.next(true);
          this.setApiTriggerTime(new Date().getTime());
          if (!!res?.access_token) {
            this.updateTokenData(res);
          }
        }),
        catchError(err => {
          console.log(err);
          this.toastr.error(
            "Email or password are incorrect. ",
            "Validation failed!",
            {
              timeOut: 3000,
              positionClass: "toast-top-left",
            }
          );
          return throwError(err);
        }),
      );
  }

  private updateTokenData(data: AuthResponse) {
    const now = new Date();
    const lastApiTriggerTime = JSON.parse(localStorage.getItem('ApiLastTriggerTime'));
    const passedExpirationTime = now.getTime() - lastApiTriggerTime;

    const expiresInDuration = data.expires_in ? (data.expires_in * 1000 - passedExpirationTime) : 1.2e6;
    console.log('expiresInDuration: ', expiresInDuration);
    const tokenDetails = jwt_decode(data.access_token);
    const tokenPayload = {
      expiresInTime: now.getTime() + expiresInDuration,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      userName: tokenDetails['preferred_username'] || '',
      userRoles: tokenDetails['resource_access']?.intellijaui?.roles || []
    };

    localStorage.setItem('Token', JSON.stringify(tokenPayload));
    this.setRefreshTokenEvent(expiresInDuration, tokenPayload.expiresIn);
  }

  setRefreshTokenEvent(expiresInDuration: number, expiresInTime: number) {
    const expiresIn = expiresInDuration > 40000 ? expiresInDuration : expiresInTime * 1000;
    this.refreshTimeout = setTimeout(() => {
      this.refreshToken();
      clearTimeout(this.refreshTimeout);
    }, expiresIn - 30000);

    this.logoutTimeout = setTimeout(() => {
      const apiLastTriggerTime = JSON.parse(localStorage.getItem('ApiLastTriggerTime'));
      const expiryTime = apiLastTriggerTime + expiresInDuration;

      if (new Date().getTime() > expiryTime) {
        this.logOut(true);
        clearTimeout(this.refreshTimeout);
        clearTimeout(this.logoutTimeout);
      }
    }, expiresInDuration + 1000);
  }

  refreshToken() {
    const tokenPayload = JSON.parse(localStorage.getItem('Token'));
    if (!tokenPayload) return;

    const payload = `grant_type=refresh_token&client_id=intellijaui&refresh_token=${tokenPayload.refreshToken}`;
    const headers = new HttpHeaders()
      .set('content-type', 'application/x-www-form-urlencoded');

    this.keyCloakApiService.auth<any>(payload, 'token', { headers })
      .pipe(
        catchError(err => {
          this.toastr.warning('Unable to refresh token');
          return EMPTY;
        }),
        take(1)
      ).subscribe((res: AuthResponse) => {
        if (res?.access_token) {
          this.updateTokenData(res);
        }
      });
  }

  logOut(disableRoutelock = false) {
    console.log('logOut :>>Method ');
    if (disableRoutelock) {
      this.routeLockService.disableAllLocks();
    }

    // close dialog model if open on logout
    this.dialog.closeAll();
    localStorage.clear();
    // reset idle user track
    this.startIdleUserTrack();
    this.idle.stop();
    // close websocket connection
    this.eventBusService.closeSocketConnection();
    this.panelService._features.next([]);
    this.router.navigate(['/auth/login']);
    this._loginSource.next(false);
  }

  static get isLoggedIn(): boolean {
    const authToken = localStorage.getItem('Token');
    return (authToken !== null);
  }

  /**
   * reset idle user track
   */
  startIdleUserTrack() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  /**
* fetch all User Groups
*/
  getFeaturesByRoles(roles: string[]) {
    return this.apiService.getGQLData({ query: getFeaturesByRoles.query, params: { roles: roles } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map(response => { return (response?.data as IGetFeaturesByRoles) }));
  }

  /**
   * get features details by features
   * @param features features
   */
  getFeaturesDetailByFeatures(features: string[]) {
    return this.apiService.getGQLData({ query: getFeaturesDetailByFeatures.query, params: { features: features } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map(response => { return (response?.data?.intellija_ref_features as IFeatures[]) }));
  }

}
